import getClassNames from 'classnames';
import React from 'react';
import Div from '../Element/Div/Div';
import Element, { CommonProps } from '../Element/Element';
import Flex from '../Flex/Flex';
import Gutter from '../Gutter/Gutter';
import BodyText from '../Typography/BodyText/BodyText';
import styles from './RadioButton.module.scss';

type RadioButtonProps = {
  /** Value of the radiobutton */
  value?: string | number;
  /** Whether the radiobutton is selected or not */
  checked?: boolean;
  /** On change handler */
  onChange?: (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLDivElement>,
    checked: boolean
  ) => void;
  /** Label for the radio button */
  label?: React.ReactNode;
  /** Description for the radio button */
  description?: string;
  /** Name of the radio button */
  name?: string;
  /** Make element readable and focusable, but not editable. Value is still submitted with a form. */
  readOnly?: boolean;
  /** Make element not mutable, focusable, or submitted with the form. */
  disabled?: boolean;
  /** Custom classname */
  className?: string;
  /** Center the radio button horizontally */
  centeredHorizontally?: boolean;
  /** Radiobutton has an error, use it with description prop to give a descriptive message for th error */
  error?: boolean;
} & Omit<CommonProps<'label'>, 'onChange'>;

const RadioButton: React.FunctionComponent<RadioButtonProps> = ({
  value,
  checked,
  onChange,
  label,
  description,
  error,
  name,
  readOnly,
  disabled,
  className,
  centeredHorizontally,
  ...props
}) => {
  const disabledOrReadOnly = readOnly || disabled;

  const classNames = [
    styles.radioButton,
    { [styles.radioButton__disabled]: disabledOrReadOnly },
    className,
  ];

  const classNamesForHiddenButton = getClassNames(
    styles.radioButton_hiddenButton,
    'radio-button-input-test'
  );

  const blur = (event: any) => {
    event.preventDefault();
  };

  return (
    <Element tagName="label" className={classNames} {...props}>
      <Flex centeredHorizontally={centeredHorizontally} onMouseDown={blur}>
        <div onMouseDown={blur}>
          <input
            name={name}
            value={value}
            type="radio"
            onChange={(event) => {
              !disabledOrReadOnly &&
                onChange &&
                onChange?.(event, event.target.checked);
            }}
            className={classNamesForHiddenButton}
            checked={checked}
            readOnly={readOnly}
            tabIndex={-1}
            disabled={disabled}
          />

          <Div
            onMouseDown={blur}
            className={[
              styles.radioButton_fakeButton,
              {
                [styles.radioButton_fakeButton__unchecked]: !checked,
                [styles.radioButton_fakeButton__checked]: !!checked,
                [styles.radioButton_fakeButton__error]: !!error,
                [styles.radioButton_fakeButton__disabled]: disabledOrReadOnly,
              },
            ]}
            tabIndex={disabled ? -1 : 0}
            onKeyPress={(e) => {
              e.key === 'Enter' && !disabledOrReadOnly && onChange?.(e, true);
            }}
            borderRadius={{ size: 'rounded' }}
            backgroundColor={disabledOrReadOnly ? 'gray200' : 'white'}
          >
            {checked && (
              <Div
                className={styles.radioButton_fakeButtonIcon}
                backgroundColor={disabledOrReadOnly ? 'gray400' : 'purple500'}
                borderRadius={{ size: 'rounded' }}
              />
            )}
          </Div>
        </div>

        <Gutter size="xs" />

        <div>
          {label && (
            <BodyText
              tagName="span"
              color={disabledOrReadOnly ? 'gray400' : 'black'}
              block
            >
              {label}
            </BodyText>
          )}

          {description && (
            <>
              <Gutter size="xxs" />
              <BodyText
                tagName="span"
                size="sm"
                className={styles.radioButton_description}
                color={
                  error ? 'red600' : disabledOrReadOnly ? 'gray400' : 'gray500'
                }
                block
              >
                {description}
              </BodyText>
            </>
          )}
        </div>
      </Flex>
    </Element>
  );
};

export default RadioButton;
